import type { CompetitorRecord } from "@/lib/compare/types";

const HELP_PRICE = "https://eklipse.gg/help/how-much-does-eklipse-premium-cost/";
const HELP_FREE = "https://eklipse.gg/help/is-eklipse-free-or-paid/";
const HELP_TRIAL = "https://eklipse.gg/help/is-there-a-free-trial-for-premium/";
const HELP_VOD = "https://eklipse.gg/help/is-there-a-maximum-vod-length-the-ai-can-process/";
const HELP_PLANS = "https://eklipse.gg/help/what-are-the-main-differences-between-the-free-and-premium-plans/";
const BLOG_PREMIUM = "https://blog.eklipse.gg/eklipse-news-and-guide/eklipse-premium-plan.html";
const REFUNDS = "https://eklipse.gg/refund-policy/";
const HOME = "https://eklipse.gg/";
const ABOUT = "https://eklipse.gg/about";
const VS_OPUS = "https://eklipse.gg/compare/eklipse-vs-opusclip/";
const TRUSTPILOT = "https://www.trustpilot.com/review/eklipse.gg?stars=1";
const CHECKED = "2026-10-08";

export const EKLIPSE: CompetitorRecord = {
  slug: "eklipse",
  name: "Eklipse",
  aliases: ["Eklipse.gg", "eklipse.gg", "Eklipse AI", "Eklipse GG"],
  url: "https://eklipse.gg",
  pricingUrl: HELP_PRICE,
  category: "streamer",
  positioning:
    "AI clip maker for gamers and streamers, described on its homepage as the only AI tool that fully automates your highlights.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "Preview scan of one recent stream, no exports",
      includes: [
        "Preview scan of one recent stream",
        "Exporting requires Premium",
        "No time-limited trial",
        "Old free tier (720p, 1 stream a day, watermark) ended June 2026",
      ],
      sourceUrl: HELP_FREE,
      checkedAt: CHECKED,
      note: "The help center, updated Aug 13, 2026, says Eklipse is a paid app; the blog says it moved to a fully paid product in June 2026.",
    },
    {
      name: "Premium",
      monthly: 24.99,
      annualPerMonth: 14.99,
      annualTotal: 179.99,
      metered: "Up to 3 streams a day, up to 45 clips per highlight output",
      includes: [
        "Up to 1080p 60fps exports, no watermark",
        "Twitch, Kick and YouTube VOD import by OAuth",
        "Content Publisher to TikTok, Shorts and Reels",
        "1000+ premium templates",
        "Priority processing (AI Speed Boost)",
        "$112.99 per 6 months; $27.99/mo in the iOS and Android apps",
      ],
      sourceUrl: HELP_PRICE,
      checkedAt: CHECKED,
      note: "No public pricing page. Figures are from the help center, updated Apr 12, 2026, and match the blog's Premium page modified Sep 27, 2026. Mobile in-app monthly is $27.99.",
    },
  ],
  entryPlanName: "Premium",
  entryPlanIncludes:
    "Up to 3 streams a day and 45 clips per output, 1080p60 exports without a watermark, Twitch, Kick and YouTube VOD import",
  freeTier:
    "None since June 2026. A free account gets a preview scan of one recent stream; exporting requires Premium.",
  trial: "None. The help center says Eklipse does not offer a time-limited free trial.",
  meteringUnit:
    "Streams per day: up to 3 on Premium, each VOD up to 6 hours (help center) or 12 hours (blog), up to 45 clips per output",
  exportResolution: "1080p 60fps on Premium with no watermark; Ultra Highlights capture up to 1440p",
  features: {
    longToShort: { v: "yes", note: "Twitch, Kick, YouTube VODs and uploads", source: HOME },
    momentRanking: { v: "yes", note: "Virality scoring per their compare table", source: VS_OPUS },
    reframing: { v: "yes", note: "AI reframe with manual override", source: "https://eklipse.gg/tool/clip-ai-reframe/" },
    maxSource: { v: "text", note: "6 hours (help center) or 12 hours (blog)", source: HELP_VOD },
    layouts: { v: "n/a", note: "Not enumerated; 1000+ templates", source: HELP_PLANS },
    redditStory: { v: "no", source: "https://eklipse.gg/compare/eklipse-vs-crayo/" },
    fakeText: { v: "no", source: HOME },
    aiImages: { v: "no", source: HOME },
    voiceover: { v: "no", note: "Voice Command triggers clips, not TTS", source: HOME },
    voiceCloning: { v: "no", source: HOME },
    avatars: { v: "no", source: HOME },
    timelineEditor: { v: "partial", note: "Eklipse Studio vertical editor", source: VS_OPUS },
    captionStyles: { v: "n/a", note: "Count not published", source: HOME },
    captionLanguages: { v: "n/a", note: "Not published", source: HOME },
    bRoll: { v: "no", source: HOME },
    fourK: { v: "no", note: "1080p60 export, 1440p capture", source: HELP_PLANS },
    scheduling: { v: "yes", note: "Content Publisher: TikTok, Shorts, Reels", source: HOME },
    analytics: { v: "no", source: HOME },
    socialTracker: { v: "no", source: HOME },
    api: { v: "no", note: "No public API found", source: HOME },
    webhooks: { v: "n/a", note: "No API, none published", source: HOME },
    web: { v: "yes", source: HOME },
    desktop: { v: "n/a", note: "Not advertised on their site", source: ABOUT },
    mobile: { v: "yes", note: "iOS and Android", source: ABOUT },
    teamSeats: { v: "no", source: HELP_PLANS },
    freeTier: { v: "no", note: "Preview scan only since June 2026", source: HELP_FREE },
    trial: { v: "no", note: "No time-limited trial, per help center", source: HELP_TRIAL },
    metering: { v: "text", note: "Up to 3 streams a day", source: BLOG_PREMIUM },
    rollover: { v: "n/a", note: "No credits; the cap is per day", source: BLOG_PREMIUM },
    refunds: { v: "text", note: "Generally not provided", source: REFUNDS },
    cancelInApp: { v: "n/a", note: "Not described on the pages checked", source: HELP_PRICE },
  },
  billingTerms: {
    refunds: "Refunds are generally not provided for any fees already paid, per eklipse.gg/refund-policy as of Oct 8, 2026.",
    cancel: "Cancellation steps are not described on the help center pricing pages or the refund policy.",
    sourceUrl: REFUNDS,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "Now it's 24.99 and you can only process 3 streams per day",
      date: "2026-02-25",
      url: TRUSTPILOT,
      evidences: "metering",
    },
    {
      text: "wouldnt refund me few days later of autorenew for a whole year",
      date: "2026-08-25",
      url: TRUSTPILOT,
      evidences: "refunds",
    },
  ],
  theirCrayoPages: [
    {
      url: "https://eklipse.gg/compare/eklipse-vs-crayo/",
      date: "2026-07-22",
      claims: [
        "Crayo does not analyze your streams",
        "Highlight detection: No, gameplay is background b-roll",
        "AI voiceover + 15+ caption styles",
        "No free tier; paid tiers from about $19/mo",
        "about $13/month billed annually",
        "Clipper and Pro plans",
        "If you want your own gameplay turned into clips, Eklipse.",
      ],
    },
    {
      url: "https://eklipse.gg/compare/crayo-alternatives/",
      date: "2026-07-01",
      claims: [
        "The best Crayo alternatives for short-form video: Eklipse, AutoShorts.ai, Revid.ai, SendShort, OpusClip, Submagic",
        "Crayo framed as a faceless AI story shorts generator only",
      ],
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: "There is no public pricing page: eklipse.gg/pricing shows no prices and the Pricing nav link opens the login-gated app.eklipse.gg/register. Every figure ($24.99/mo, $112.99 per 6 months, $179.99/yr, about $14.99/mo, $27.99/mo in-app) was fetched from Eklipse's own help center page updated Apr 12, 2026, and matches the blog's Premium page modified Sep 27, 2026. The blog's free-vs-paid post says about $12.50/mo on annual, which conflicts; the help center figure is used. The 3 streams a day, 45 clips per output and 12-hour VOD limits come from the blog; the help center says 6 hours for Twitch and Kick. A human browser check of the logged-in checkout would confirm the live price.",
  },
  seoPattern:
    "eklipse.gg/compare/eklipse-vs-<x>/, /compare/<x>-alternatives/ and /compare/eklipse-as-<x>-alternative/, including eklipse-vs-crayo and crayo-alternatives",
};
